import {debounce} from "@plaudit/frontend-apis";
import queryLoopLoaded from "@plaudit/query-loop.api";

import QueryLoopFormFieldBase from "./QueryLoopFormFieldBase";

let geocoder: google.maps.Geocoder|undefined = undefined;

export class LocationSearchQueryLoopFormField extends QueryLoopFormFieldBase {
	private readonly backingInput: HTMLInputElement;
	private readonly locationInput: HTMLInputElement;
	private readonly geocodeQueue = { currentId: 0, queue: Promise.resolve() };

	public constructor(field: HTMLFieldSetElement, type: string) {
		super(field, type);
		const backingInput = field.querySelector('.plauditQueryLoopField__input');
		const locationInput = field.querySelector('.plauditQueryLoopField__locationInput');
		if (!(backingInput instanceof HTMLInputElement && locationInput instanceof HTMLInputElement)) {
			throw new Error("Attempted to attach an invalid field");
		}
		this.backingInput = backingInput;
		this.locationInput = locationInput;

		if (!("geolocation" in navigator)) {
			for (const element of field.querySelectorAll('.plauditQueryLoopField__currentLocationButton--requiresGeolocation')) {
				if ("disabled" in element) {
					element.disabled = true;
				}
			}
		}

		const debouncer = debounce((_?) => this.geocodeAndUpdate(this.locationInput.value),
			(e?) => e instanceof KeyboardEvent && e.key === 'Enter' ? 50 : 500);
		this.locationInput.addEventListener('keyup', debouncer);
		this.locationInput.addEventListener('keypress', e => {
			if (e.key === 'Enter') {
				e.preventDefault();
			}
			debouncer();
		});
		for (const button of this.field.querySelectorAll('.plauditQueryLoopField__currentLocationButton')) {
			button.addEventListener('click', () => this.setToCurrentLocation());
		}
		this.postConstruct();
	}

	private setBackingInputValue(value: string) {
		this.backingInput.value = value;
		this.backingInput.dispatchEvent(new Event('input', {bubbles: true, cancelable: true}));
	}

	public setToCurrentLocation() {
		this.geocodeAndUpdate(this.locationInput.value = 'Current Location');
	}

	public getValue(): string {
		return this.backingInput.value;
	}
	public setValue(value: string|string[]): boolean {
		if (Array.isArray(value)) {
			value = value.join(' ');
		}
		if (this.backingInput.value !== value) {
			this.backingInput.value = value;
			//TODO: value must be <displayed location>:lat,lon at this point. Add validation?
			this.locationInput.value = value.substring(0, value.lastIndexOf(':'));
			this.updateValueIsDefaultFlag();
			return true;
		}
		return false;
	}

	public loadIntoQuery(params: URLSearchParams) {
		this.replaceMyPotentialPHPArrayParametersWithFirstValue(params);
		return this.loadIndividualValueIntoQuery(this.getValue(), params);
	}
	public loadFromQuery(params: URLSearchParams) {
		// We are overriding whatever was currently happening in the queue, so we ensure that the queue's results from before this operation won't be used and mark the field as not geocoding.
		this.geocodeQueue.currentId++;
		this.field.classList.toggle('plauditQueryLoopField--geocoding', false);
		if (this.backingInput.value !== params.get(this.backingInput.name)) {
			this.backingInput.value = params.get(this.backingInput.name) ?? '';
			return true;
		}
		return false;
	}

	public getLabeledValues(selectedOnly?: boolean) {
		return selectedOnly && !this.getValue() ? [] : [{field: this.name, value: this.backingInput.value, label: this.locationInput.value}];
	}

	protected getDefaultValue(): string|string[] {
		return this.backingInput.getAttribute('data-default') ?? '';
	}

	private geocodeAndUpdate(location: string) {
		const loc = location.trim();
		const myId = ++this.geocodeQueue.currentId;
		this.geocodeQueue.queue = this.geocodeQueue.queue.then(() => {
			if (this.geocodeQueue.currentId === myId) {
				if (this.backingInput.value.substring(0, this.backingInput.value.lastIndexOf(':')) === loc) {
					return;
				}
				this.field.classList.toggle('plauditQueryLoopField--geocoding', true);
				if (!loc) {
					this.setBackingInputValue('');
				} else if (loc === 'Current Location' && navigator.geolocation) {
					return new Promise<void>(resolve => {
						if (this.geocodeQueue.currentId === myId) {
							navigator.geolocation.getCurrentPosition(position => {
								if (this.geocodeQueue.currentId === myId) {
									this.setBackingInputValue(`${loc}:${position.coords.latitude},${position.coords.longitude}`);
								}
								resolve();
							}, () => resolve());
						}
					}).finally(() => {
						if (this.geocodeQueue.currentId === myId) {
							this.field.classList.toggle('plauditQueryLoopField--geocoding', false);
						}
					});
				} else {
					const zipCode = /^([0-9]{5})(?:-[0-9]{4})?$/.exec(loc);
					if (zipCode !== null) {
						return fetch(new URL(`https://secure.geonames.org/postalCodeSearchJSON?postalcode=${zipCode[1]}&country=US&style=SHORT&username=plaudit`))
							.then(data => data.json())
							.then(({postalCodes}: {postalCodes?: Array<Record<string, unknown>>}) => {
								if (!postalCodes?.length) {
									return this.useGoogleGeocoder(loc, myId);
								} else if (this.geocodeQueue.currentId === myId) {
									this.setBackingInputValue(`${loc}:${postalCodes[0]['lat']},${postalCodes[0]['lng']}`);
									this.field.classList.toggle('plauditQueryLoopField--geocoding', false);
								}
								return undefined;
							});
					} else {
						return this.useGoogleGeocoder(loc, myId);
					}
				}
			}
			return;
		});
	}
	private useGoogleGeocoder(loc: string, myId: number) {
		if (this.geocodeQueue.currentId !== myId) {
			return Promise.resolve();
		}
		if (!geocoder) {
			if (window.google.maps.Geocoder) {
				geocoder = new google.maps.Geocoder();
			} else {
				this.field.classList.toggle('plauditQueryLoopField--geocoding', false);
				return Promise.resolve();
			}
		}
		const componentRestrictions = this.locationInput.getAttribute("data-component-restrictions");
		return geocoder.geocode({address: loc, componentRestrictions: componentRestrictions ? JSON.parse(componentRestrictions) : null})
			.then(({results}) => {
				if (this.geocodeQueue.currentId === myId) {
					this.setBackingInputValue(results.length === 0 ? '' : `${loc}:${results[0].geometry.location.toUrlValue()}`);
					this.field.classList.toggle('plauditQueryLoopField--geocoding', false);
				}
			}, () => {
				if (this.geocodeQueue.currentId === myId) {
					this.field.classList.toggle('plauditQueryLoopField--geocoding', false);
				}
			});
	}

	public isSingleValued() {
		return true;
	}
}

queryLoopLoaded.then(() => {
	plaudit.queryLoop.fieldTypes.set('location', field => new LocationSearchQueryLoopFormField(field, 'location'));
});
